import { DeepPartial } from 'react-hook-form';
import { DirectSignupFilters, ResumedLeadState } from '../context';
import { Contact } from 'domains/contact/types';
import { EvaluationScheduleFormType } from '../schemas/evaluationScheduleSchema';
import { TherapyTargetValue } from '../misc/constants';
import { ContactRelationship } from 'domains/client';
import { EligibilityCheckResult } from '../SyntaxComps/EligibilityCard';
import { InsurancePayment } from '../schemas/filtersSchema';
import { get } from 'lodash';
import { EvaluationFormType, getInsuranceSchemaType } from '../schemas/evaluationSchema';
import { PCPInputMode } from './Insurance/PCP/pcpSchemas';
import { isPCPRequired } from '../businessLogic/isPCPRequired';

const getDefaultRelationshipToClient = (
  target: TherapyTargetValue,
): EvaluationFormType['contactInfo']['relationshipToClient'] | undefined => {
  switch (target) {
    case 'adult':
      return ContactRelationship.Client;
    default:
      return undefined;
  }
};

/** Merges various sources of information and calculates the default values for the evaluation form */
export const getEvaluationFormDefaultValues = (data: {
  evaluationSchedule: EvaluationScheduleFormType;
  filters: DirectSignupFilters;
  incompleteEvalForm?: DeepPartial<EvaluationFormType>;
  existingContactData?: Contact;
  resumedLeadState?: ResumedLeadState;
}): DeepPartial<EvaluationFormType> => {
  const { resumedLeadState, evaluationSchedule, filters, incompleteEvalForm, existingContactData } = data;
  const { paymentMethod } = filters;

  return {
    loginContactInfo: getLoginContactInfo(incompleteEvalForm, existingContactData),
    leadId: resumedLeadState?.leadID,
    evaluationSchedule: evaluationSchedule,
    clientType: filters.therapyTarget,
    concerns: {
      clinicalAreas: filters.clinicalAreas,
      clinicalConcernKeys: filters.clinicalConcernKeys,
    },
    clientInfo: {
      language: filters.language,
      stateCode: filters.stateCode,
      firstName: incompleteEvalForm?.clientInfo?.firstName,
      lastName: incompleteEvalForm?.clientInfo?.lastName,
      dateOfBirth: incompleteEvalForm?.clientInfo?.dateOfBirth,
    },
    contactInfo: {
      relationshipToClient:
        incompleteEvalForm?.contactInfo?.relationshipToClient ?? getDefaultRelationshipToClient(filters.therapyTarget),

      ...getContactInfo(incompleteEvalForm, existingContactData),
    },
    primaryPhysician: {
      mode: getDefaultPCPMode(data),
      isPCPRequired: isPCPRequired(filters.paymentMethod),
    },
    billingInfo: {
      ...(paymentMethod.type === 'insurance' && {
        type: getInsuranceSchemaType(paymentMethod.insuranceData.insuranceType),
        insuranceData: (filters.paymentMethod as InsurancePayment).insuranceData,

        eligibilityCheckResult: resumedLeadState?.shouldSkipEligibility
          ? EligibilityCheckResult.IN_NETWORK
          : (filters.paymentMethod as InsurancePayment).insuranceData.networkStatus === 'Out-of-network'
          ? EligibilityCheckResult.OUT_OF_NETWORK
          : undefined,
        subscriber:
          incompleteEvalForm?.billingInfo?.type === 'insurance-commercial'
            ? incompleteEvalForm?.billingInfo.subscriber
            : undefined,
        memberId: get(incompleteEvalForm, 'billingInfo.memberId'),
      }),
      ...(paymentMethod.type === 'privatePay' && {
        type: 'privatePay',
      }),
    } as EvaluationFormType['billingInfo'],
  };
};

const getDefaultPCPMode = ({ filters }: Parameters<typeof getEvaluationFormDefaultValues>[0]) => {
  const PCPRequired = isPCPRequired(filters.paymentMethod);

  if (filters.paymentMethod.type !== 'insurance') {
    return PCPInputMode.disabled;
  }

  /** Note(juan): this is needed because OON doesn't run eligibility check, so we need to auto-populate PCP for OON plans when PCP is required right away */
  if (filters.paymentMethod.insuranceData.networkStatus === 'Out-of-network' && PCPRequired) {
    return PCPInputMode.Auto;
  }

  return PCPInputMode.disabled;
};

const getContactInfo = (
  incompleteEvalForm?: DeepPartial<EvaluationFormType>,
  existingContactData?: Contact,
): DeepPartial<EvaluationFormType['contactInfo']> => {
  if (incompleteEvalForm?.loginContactInfo?.isAlreadyContact && !existingContactData) {
    // Used in case of logging out mid flow, clear section to 0
    return {};
  }

  if (existingContactData) {
    return {
      firstName: existingContactData.contactFirstName,
      lastName: existingContactData.contactLastName,
      phone: existingContactData.phone?.mobile ?? '',
    };
  }
  return {
    firstName: incompleteEvalForm?.contactInfo?.firstName,
    lastName: incompleteEvalForm?.contactInfo?.lastName,
    phone: incompleteEvalForm?.contactInfo?.phone,
  };
};

const getLoginContactInfo = (
  incompleteEvalForm?: DeepPartial<EvaluationFormType>,
  existingContactData?: Contact,
): DeepPartial<EvaluationFormType['loginContactInfo']> => {
  if (incompleteEvalForm?.loginContactInfo?.isAlreadyContact && !existingContactData) {
    // Used in case of logging out mid flow, clear section to 0
    return {};
  }

  if (existingContactData) {
    return {
      isAlreadyContact: true,
      contactId: existingContactData.contactID,
      email: existingContactData.email,
    };
  }

  return {
    ...incompleteEvalForm?.loginContactInfo,
  };
};
